import { html } from "htm/preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { APP_NAME } from "../copy.js";
import { prefersReducedMotion } from "../animation.js";

/**
 * How long a tap's replay runs. The splash in index.html plays the same shape over
 * 2.6s because it's looping under an unbounded page load and can afford a long tail;
 * a tap has been asked for, so it answers faster. Keyframe *shapes* are shared with
 * the `mark-*` rules in styles.css — change one, change both.
 */
const REPLAY_MS = 1150;

/**
 * The Divot mark: the struck ball climbing away, and the wedge of turf it tore out
 * left behind at the bottom of the swing.
 *
 * Drawn inline rather than loaded as an <img> so the theme can colour it — the ball
 * takes the surrounding text colour and the turf and flight take the accent (see
 * `.brand-mark` in styles.css). The same geometry is in public/brand/mark.svg,
 * build/icons.mjs (which rasterizes the app icons and the link-preview card), and the
 * boot splash in public/index.html; change one, change all four.
 *
 * `replay` is a counter, not a boolean: replaying is an event, and a boolean can't
 * express "again". Bumping it plays the strike the mark is a freeze-frame of — the
 * ball is sitting in the gap between the two ground strokes (that gap is the divot),
 * it gets hit, the turf tears out and tumbles clear, and the ball climbs away with the
 * flight drawing in behind it as its trail. Nothing fades in; the ball is on screen
 * throughout, because a ball that materialises at the end of an arc reads as delivered
 * rather than struck.
 *
 * At 0 it stays still, which is what the copy on the Home screen wants.
 */
export function Mark({
  size = 26,
  class: className,
  replay = 0,
}: {
  size?: number;
  class?: string;
  replay?: number;
}) {
  const wedge = useRef<SVGGElement | null>(null);
  const flight = useRef<SVGPathElement | null>(null);
  const ball = useRef<SVGCircleElement | null>(null);

  useLayoutEffect(() => {
    if (replay === 0 || prefersReducedMotion()) return;
    if (!wedge.current || !flight.current || !ball.current) return;

    // Web Animations rather than toggling a class: a CSS animation won't replay on a
    // second tap unless the class is removed and a reflow forced between the two
    // writes, and animate() also leaves the element's own styles alone so it composes
    // with the static rules instead of fighting them (same reasoning as useFlipList).
    //
    // `linear` on purpose. The easing is in where the offsets sit — they follow
    // 1-sqrt(1-t), so the ball leaves the face fast and decelerates as it climbs.
    // Stacking a timing function on top would ease the easing.
    const options = { duration: REPLAY_MS, easing: "linear" };
    const running = [
      // Torn out at impact and thrown clear. It doesn't exist before contact, so it
      // arrives on a hard cut at the ball's position rather than fading up out of
      // nothing, then tumbles past its resting angle and settles back.
      wedge.current.animate(
        [
          { transform: "translate(10px, 10px) rotate(40deg) scale(0.25)", opacity: 0, offset: 0 },
          { transform: "translate(10px, 10px) rotate(40deg) scale(0.25)", opacity: 0, offset: 0.06 },
          { transform: "translate(10px, 10px) rotate(40deg) scale(0.25)", opacity: 1, offset: 0.0601 },
          { transform: "translate(2px, -1px) rotate(-12deg) scale(1.05)", opacity: 1, offset: 0.19 },
          { transform: "none", opacity: 1, offset: 0.42 },
        ],
        options,
      ),
      // The trail, drawing in behind the ball. Each offset is 1 minus that keyframe's
      // position along the path, at the same times as the ball below — so the tip of
      // the arc stays pinned to the ball instead of racing or lagging it.
      //
      // strokeDasharray is set here rather than in the stylesheet so the mark at rest
      // keeps plain undashed geometry. `pathLength="1"` below is what makes 1 the
      // whole path, so retuning the curve can't strand a measured length in here.
      flight.current.animate(
        [
          { strokeDasharray: "1 2", strokeDashoffset: "1", offset: 0 },
          { strokeDasharray: "1 2", strokeDashoffset: "1", offset: 0.06 },
          { strokeDasharray: "1 2", strokeDashoffset: "0.75", offset: 0.159 },
          { strokeDasharray: "1 2", strokeDashoffset: "0.5", offset: 0.277 },
          { strokeDasharray: "1 2", strokeDashoffset: "0.25", offset: 0.43 },
          { strokeDasharray: "1 2", strokeDashoffset: "0", offset: 0.8 },
        ],
        options,
      ),
      // Struck, not delivered: the ball is on screen the whole time. It starts in the
      // gap between the two ground strokes — that gap is the divot scar — and rides
      // the flight path's own cubic up to where the static mark holds it. The offsets
      // are that curve sampled at t = 0, 1/4, 1/2, 3/4, 1, relative to its rest.
      ball.current.animate(
        [
          { transform: "translate(-30px, 32px)", offset: 0 },
          { transform: "translate(-30px, 32px)", offset: 0.06 },
          { transform: "translate(-25.4px, 22.3px)", offset: 0.159 },
          { transform: "translate(-19.1px, 14.3px)", offset: 0.277 },
          { transform: "translate(-11.6px, 8.1px)", offset: 0.43 },
          { transform: "none", offset: 0.8 },
        ],
        options,
      ),
    ];

    // Cancelling on cleanup is what makes a second tap restart cleanly rather than
    // stacking a new animation on top of one still in flight.
    return () => {
      for (const animation of running) animation.cancel();
    };
  }, [replay]);

  return html`
    <svg
      class=${className ? `brand-mark ${className}` : "brand-mark"}
      width=${size}
      height=${size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <g class="mark-turf" fill="currentColor" stroke="currentColor">
        <g fill="none" stroke-width="6.5" stroke-linecap="round">
          <path d="M5 54h13" />
          <path d="M33 54h26" />
        </g>
        <g class="mark-wedge" ref=${wedge}>
          <rect x="7" y="36.5" width="16" height="7" rx="3.5" stroke="none" transform="rotate(-28 15 40)" />
        </g>
      </g>
      <path
        class="mark-flight"
        ref=${flight}
        pathLength="1"
        fill="none"
        stroke="currentColor"
        stroke-width="7"
        stroke-linecap="round"
        d="M25 50c5-14 15-24 27-28"
      />
      <circle class="mark-ball" ref=${ball} cx="55" cy="18" r="7.5" fill="currentColor" />
    </svg>
  `;
}

/** Mark plus name, as one lockup. The name is live text, not paths, so it renders
 * in the same system font as the rest of the app at any size.
 *
 * The counter starts at 1, so the mark plays its strike once as the app opens. The
 * boot splash in index.html animates too, but it's replaced the instant app.js lands —
 * on any healthy connection that's a few frames, which is no time to see anything. The
 * splash is for slow loads; this is the copy that actually gets watched.
 *
 * AppShell keeps the app bar mounted across routes, so that's once per app open rather
 * than once per navigation — and it's also why tapping can replay the strike without
 * competing with the navigation the same tap triggers: the mark isn't torn down
 * mid-flight by the screen change. */
export function Wordmark({ size = 26, onClick }: { size?: number; onClick?: () => void }) {
  const [replay, setReplay] = useState(1);

  return html`
    <button
      class="app-brand"
      onClick=${() => {
        setReplay((n) => n + 1);
        onClick?.();
      }}
      aria-label=${`${APP_NAME} — home`}
    >
      <${Mark} size=${size} replay=${replay} />
      <span>Divot</span>
    </button>
  `;
}
